import { useState } from "react";
import { Loader2, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ModalShell } from "@/components/ui/modal-shell";
import { customFetch } from "@workspace/api-client-react";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { extractApiErrorMessage } from "@/lib/api-error";

// Conta multi-loja (jul/2026): cria uma LOJA INDEPENDENTE (grátis, sem matriz)
// vinculada ao dono logado. Substitui o CreateUnitModal (filial paga) — que
// fica no ar só até a migração das unidades legadas.
interface CreateStoreModalProps {
  open: boolean;
  onClose: () => void;
  /** Chamado com o id da loja criada — o chamador faz o switch pra ela. */
  onCreated: (storeId: string) => void;
}

const STORE_TYPES = [
  { value: "produtos", label: "Produtos" },
  { value: "servicos", label: "Serviços" },
  { value: "ambos", label: "Ambos" },
] as const;

export function CreateStoreModal({ open, onClose, onCreated }: CreateStoreModalProps) {
  const [name, setName] = useState("");
  const [useAccountWhatsapp, setUseAccountWhatsapp] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [whatsappDdi, setWhatsappDdi] = useState("55");
  const [storeType, setStoreType] = useState<"produtos" | "servicos" | "ambos">("produtos");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await customFetch<{ store: { id: string; name: string; slug: string } }>(
        "/api/units/store",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.trim(),
            storeType,
            ...(useAccountWhatsapp || !whatsappNumber.trim()
              ? {}
              : { whatsappNumber: `${whatsappDdi}${whatsappNumber.replace(/\D/g, "")}` }),
          }),
        },
      );
      setName("");
      setWhatsappNumber("");
      setUseAccountWhatsapp(true);
      onCreated(resp.store.id);
    } catch (e) {
      setError(extractApiErrorMessage(e, "Não foi possível criar a loja — tente de novo."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Nova loja"
      subtitle="Outra loja na mesma conta — grátis, com catálogo e link próprios"
      size="md"
      footer={
        <>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Store className="w-4 h-4 mr-1" /> Criar loja</>)}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
            Nome da loja <span className="text-destructive">*</span>
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Pizzaria do Centro"
            maxLength={60}
            autoFocus
          />
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1.5 block">O que essa loja oferece?</label>
          <div className="grid grid-cols-3 gap-2">
            {STORE_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setStoreType(t.value)}
                className={`h-9 rounded-lg border text-sm font-medium transition-colors ${
                  storeType === t.value
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background hover:bg-muted text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <input
              type="checkbox"
              checked={useAccountWhatsapp}
              onChange={(e) => setUseAccountWhatsapp(e.target.checked)}
              className="accent-[var(--primary)] w-4 h-4"
            />
            Receber pedidos no mesmo WhatsApp da conta
          </label>
          {!useAccountWhatsapp && (
            <div className="mt-2">
              <WhatsAppInput
                value={whatsappNumber}
                onChange={setWhatsappNumber}
                ddi={whatsappDdi}
                onDdiChange={setWhatsappDdi}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Precisa ser um número seu (da conta ou de outra loja sua).
              </p>
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
        )}
      </div>
    </ModalShell>
  );
}
